// @flow
import * as React from 'react';
import { sprintf } from 'sprintf-js';
import { getJson } from 'utils/http';
import type { Location } from 'react-router';
import { trackClicked } from 'utils/analytics';
import { algoliaTrackClick } from 'utils/algoliaSearch';
import type { CurrentUser } from 'api/currentUser';
import SearchFiltersSidebarContainer from 'containers/SearchFiltersSidebarContainer';
import SaveListingContainer from 'containers/SaveListingContainer';
import SearchHit from 'components/SearchHit';
import FeaturedSearchHit from 'components/FeaturedSearchHit';
import SortMenu from 'components/SortMenu';
import Pagination from 'components/Pagination';
import PostListingLink from 'components/PostListingLink';
import Divider from 'components/Divider';
import Icon from 'components/Icon';
import { MockResults } from 'components/SearchLoadingSection';
import ListingCountsContainer from 'containers/ListingCountsContainer';
import SearchEmptyResultsMessage from 'components/MainSearchEmptyResultsMessage';
import type { State as MainSearch } from 'redux/main/reducers/mainSearch';
import { scrollToTop } from 'utils/scroll';
import Box from 'components/Box';
import Hide from 'components/Hide';
import Text, { BodySmall } from 'components/Text';
import EmailAlertButton from 'components/EmailAlertButton';
import PromoAccordion from 'components/PromoAccordion';
import ResumeDatabaseSidebarAd from 'components/ResumeDatabaseSidebarAd';
import GradSchoolsAd from 'components/Ads/GradSchoolsAd';
import type { HeroListingType } from 'api/cms';
import {
  LISTING_TYPE_HUMAN_NAME_MAP,
  LISTING_TYPE_PLURAL_HUMAN_NAME_MAP
} from 'utils/constants';
import { clearSearchLocation } from '../redux/main/actions/currentUser';
import { gutterWidth } from '../theme';
import { results as resultsFn } from '../redux/main/reducers/mainSearch';

const { useEffect, useState } = React;

const selectedTypeName = (selectedType, resultCount) => {
  const hasOneResult = resultCount === '1';
  if (selectedType === 'ALL') {
    /* TRANSLATORS: name used for all listing types in search results (e.g. '50 opportunities match your search') */

    return hasOneResult ? getText('opportunity') : getText('opportunities');
  }
  if (selectedType === 'IDEALIST_DAY_EVENT') {
    return hasOneResult ? getText('activity') : getText('activities');
  }
  if (selectedType === 'VOLOP') {
    return hasOneResult ? getText('opportunity') : getText('opportunities');
  }
  return hasOneResult
    ? LISTING_TYPE_HUMAN_NAME_MAP[selectedType]
    : LISTING_TYPE_PLURAL_HUMAN_NAME_MAP[selectedType];
};

const ResultCount = ({
  resultCount,
  selectedType
}: {
  resultCount: string,
  selectedType: HeroListingType
}) => (
  <Text
    display={['block', 'block', 'block', 'inline-block']}
    fontSize={['20px', null, null, '16px']}
    mb={['20px', '20px', '20px', 0]}
    textAlign={['center', 'center', 'center', 'left']}
  >
    {/* TRANSLATORS: Home Page - search results description */ sprintf(
      getText('%(resultCount)s %(listingType)s found'),
      {
        resultCount,
        listingType: selectedTypeName(selectedType, resultCount)
      }
    )}
  </Text>
);

type Props = {
  clearAllFilters: Function,
  location: Location,
  searchRadius: string,
  searchLocation: SearchLocation,
  mainSearch: MainSearch,
  getSearchTrackingData: () => Object,
  clearSearchLocation: typeof clearSearchLocation,
  scrollToId: string,
  showListingCounts: boolean,
  saveSearch: Function,
  currentSearchIsSaved: boolean,
  saveableSearch: boolean,
  setPageIndex: Function,
  setSearchFilter: Function,
  pushFlashMessage: Function,
  currentUser: CurrentUser,
  featuredJob?: Object,
  featuredJobsLoading: boolean
};

const MainSearchResults = (props: Props) => {
  const {
    clearAllFilters,
    getSearchTrackingData,
    mainSearch,
    location,
    scrollToId,
    searchLocation,
    showListingCounts,
    saveSearch,
    currentSearchIsSaved,
    saveableSearch,
    setPageIndex,
    setSearchFilter,
    pushFlashMessage,
    currentUser,
    featuredJob,
    featuredJobsLoading
  } = props;

  const {
    pageIndex,
    resultsByPage,
    queryID,
    isSearching,
    filters,
    lastLoadedPageIndex,
    query
  } = mainSearch;

  const [gradPrograms, setGradPrograms] = useState([]);

  useEffect(() => {
    getJson('/data/website/random-grad-programs').then(
      ({ gradPrograms: randomGradPrograms }) =>
        setGradPrograms(randomGradPrograms)
    );
  }, []);

  useEffect(
    () => {
      if (
        mainSearch.autosaveSearch &&
        !mainSearch.autosaveDisabled &&
        !currentSearchIsSaved
      ) {
        saveSearch(true);
      }
    },
    [
      saveSearch,
      currentSearchIsSaved,
      mainSearch.autosaveSearch,
      mainSearch.autosaveDisabled,
      pushFlashMessage
    ]
  );

  useEffect(
    () => {
      scrollToTop();
    },
    [pageIndex]
  );

  const selectedType = filters.type;

  const results = resultsFn(mainSearch);
  const lastResults = resultsByPage[lastLoadedPageIndex.toString()];
  const { hits, nbHits, hitsPerPage, page, nbPages } = results || {};
  const resultCount =
    typeof nbHits === 'number' ? nbHits.toLocaleString(USER_LOCALE) : '0';

  const hasGradSchoolsAd =
    USER_LOCALE === 'en' &&
    ['JOB', 'INTERNSHIP', 'VOLOP', 'ORG'].includes(selectedType);

  const SearchResults = featuredJobsLoading ? (
    <MockResults hideLabel={selectedType !== 'ALL'} />
  ) : hits && hits.length > 0 && queryID ? (
    <>
      {hits.map((hit, index) => (
        <React.Fragment key={hit.objectID}>
          <SaveListingContainer
            listingId={hit.objectID}
            listingType={hit.type}
            listingName={hit.name}
            orgId={hit.orgID || null}
            orgType={hit.orgType || null}
            render={({ saveListing, unsaveListing, saved, canSave }) => (
              <SearchHit
                hit={hit}
                currentUser={currentUser}
                scrollIntoView={hit.objectID === scrollToId}
                saveListing={saveListing}
                unsaveListing={unsaveListing}
                saved={saved}
                canSave={canSave}
                showLabel={selectedType === 'ALL'}
                applications={
                  currentUser && currentUser.id
                    ? currentUser.listingApplications.map(({ id }) => id)
                    : []
                }
                trackClick={() => {
                  algoliaTrackClick(
                    queryID,
                    hit.objectID,
                    // Position in algolia analytics is 1-indexed
                    hitsPerPage ? page * hitsPerPage + index + 1 : index + 1
                  );
                  trackClicked('Search Result', {
                    org_id: hit.orgID,
                    org_type: hit.orgType,
                    result_id: hit.objectID,
                    result_index: index,
                    result_type: hit.type,
                    action_type: hit.actionType,
                    action_sub_type: hit.actionSubType,
                    ...getSearchTrackingData()
                  });
                }}
              />
            )}
          />
          {hasGradSchoolsAd &&
            ((hits.length <= 5 && index === hits.length - 1) || index === 4 ? (
              <GradSchoolsAd
                programs={gradPrograms}
                setSearchFilter={setSearchFilter}
              />
            ) : hits.length === 20 && index === 14 ? (
              <GradSchoolsAd
                programs={gradPrograms}
                setSearchFilter={setSearchFilter}
              />
            ) : null)}
        </React.Fragment>
      ))}
      {typeof nbPages === 'number' &&
        typeof page === 'number' &&
        typeof hitsPerPage === 'number' && (
          <Pagination
            nbHits={nbHits}
            nbPages={nbPages}
            pageIndex={page}
            setPageIndex={setPageIndex}
            hitsPerPage={hitsPerPage}
          />
        )}
    </>
  ) : isSearching ? (
    <MockResults />
  ) : (
    <SearchEmptyResultsMessage
      query={lastResults ? lastResults.query : ''}
      filters={filters}
      searchLocation={searchLocation}
      location={location}
      clearAllFilters={clearAllFilters}
    />
  );

  const postJobLink =
    selectedType === 'JOB' ? (
      <PostListingLink
        onClick={() => {
          trackClicked('Post A Listing', {
            context: 'Main Search Results'
          });
        }}
      >
        <Box classes={['flex', 'align-center']}>
          <Box mt="1px" opacity="0.8">
            <Icon
              name="pin"
              width={[12, null, 16]}
              height={[12, null, 16]}
              fill="currentColor"
            />
          </Box>
          <Text ml="0.6em" mr="0.3em" fontSize="inherit">
            {getText('Post a Job')}
          </Text>
        </Box>
      </PostListingLink>
    ) : null;

  return (
    <Box position="relative" display="flex" flexWrap="wrap">
      {showListingCounts && (
        <Box
          width="100%"
          position="relative"
          px={[gutterWidth / 2, null, null, gutterWidth]}
        >
          <ListingCountsContainer />
          <Divider mt={20} mb={48} light />
        </Box>
      )}
      <Box width={[1, 1, 1, 1 / 3]} px={[0, 0, 0, gutterWidth]}>
        <Hide lg xl>
          <BodySmall lineHeight={1.25}>
            <ResultCount
              resultCount={resultCount}
              selectedType={selectedType || 'JOB'}
            />
            {query && (
              <Box
                mt={12}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Box ml={15}>{getText('Sort by')}</Box>
                <SortMenu
                  options={[
                    {
                      name: getText('Best Match'),
                      param: 'relevance'
                    },
                    {
                      name: getText('Newest'),
                      param: 'newest'
                    }
                  ]}
                />
              </Box>
            )}
            <Box
              mt="11px"
              mb="31px"
              display="flex"
              alignItems="center"
              flexDirection="column"
              justifyContent="center"
            >
              {saveableSearch && (
                <EmailAlertButton
                  key={queryID}
                  primary={
                    (typeof nbHits !== 'number' || nbHits === 0) &&
                    !currentSearchIsSaved
                  }
                  m="5px"
                  onClick={e => {
                    e.stopPropagation();
                    saveSearch();
                    trackClicked('Get Email Alerts');
                  }}
                  saved={currentSearchIsSaved}
                />
              )}
              {postJobLink && <Box mt={16}>{postJobLink}</Box>}
            </Box>
          </BodySmall>
        </Hide>

        {selectedType === 'JOB' && (
          <Hide
            xs={!currentUser.isLoaded || !!currentUser.id}
            sm={!currentUser.isLoaded || !!currentUser.id}
            md={!currentUser.isLoaded || !!currentUser.id}
          >
            <ResumeDatabaseSidebarAd currentUser={currentUser} />
          </Hide>
        )}

        <SearchFiltersSidebarContainer />
      </Box>
      <Box
        width={[1, 1, 1, 2 / 3]}
        px={[gutterWidth / 2, gutterWidth / 2, gutterWidth / 2, gutterWidth]}
        py={[0, 0, gutterWidth / 2, 0]}
        data-qa-id="search-results"
      >
        <Hide xs sm md>
          <BodySmall mb={0}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" flex="0 1 auto">
                {USER_LOCALE === 'en' && (
                  <ResultCount
                    resultCount={resultCount}
                    selectedType={selectedType || 'JOB'}
                  />
                )}
                <Box ml={USER_LOCALE === 'en' ? 24 : -12} my={-11}>
                  <SortMenu
                    options={[
                      {
                        name: getText('Best Match'),
                        param: 'relevance'
                      },
                      {
                        name: getText('Newest'),
                        param: 'newest'
                      }
                    ]}
                  />
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                flex={USER_LOCALE === 'en' ? '0 0 auto' : '1 1 auto'}
                pl={[0, null, null, 16]}
              >
                {postJobLink && <Box mr={24}>{postJobLink}</Box>}
                {saveableSearch && (
                  <EmailAlertButton
                    key={queryID}
                    primary={
                      (typeof nbHits !== 'number' || nbHits === 0) &&
                      !currentSearchIsSaved
                    }
                    onClick={e => {
                      e.stopPropagation();
                      saveSearch();
                      trackClicked('Get Email Alerts');
                    }}
                    data-qa-id={!currentSearchIsSaved && 'save-search'}
                    saved={currentSearchIsSaved}
                  />
                )}
              </Box>
            </Box>
          </BodySmall>
          <Divider light mt={24} mb={32} />
          {USER_LOCALE !== 'en' && (
            <Box mt={-24} mb={32}>
              <ResultCount
                resultCount={resultCount}
                selectedType={selectedType || 'JOB'}
              />
            </Box>
          )}
        </Hide>
        {(selectedType === 'GROUP' ||
          selectedType === 'IDEALIST_DAY_EVENT') && (
          <PromoAccordion
            mt="24px"
            mb={hits && hits.length > 0 && queryID ? '8px' : 48}
            type={selectedType}
          />
        )}
        {!featuredJobsLoading && featuredJob && (
          <Box mb={32}>
            <SaveListingContainer
              listingId={featuredJob.objectID}
              listingType={featuredJob.type}
              listingName={featuredJob.name}
              orgId={featuredJob.orgID || null}
              orgType={featuredJob.orgType || null}
              render={({ saveListing, unsaveListing, saved, canSave }) => (
                <FeaturedSearchHit
                  key={featuredJob.objectID}
                  hit={featuredJob}
                  currentUser={currentUser}
                  saveListing={saveListing}
                  unsaveListing={unsaveListing}
                  saved={saved}
                  canSave={canSave}
                  applications={
                    currentUser && currentUser.id
                      ? currentUser.listingApplications.map(({ id }) => id)
                      : []
                  }
                  trackClick={() => {
                    trackClicked('Search Result', {
                      org_id: featuredJob.orgID,
                      org_type: featuredJob.orgType,
                      result_id: featuredJob.objectID,
                      promoted_result_index: 0,
                      result_is_promoted: true,
                      result_type: featuredJob.type,
                      action_type: featuredJob.actionType,
                      action_sub_type: featuredJob.actionSubType,
                      featured: true,
                      ...getSearchTrackingData()
                    });
                  }}
                />
              )}
            />
          </Box>
        )}
        {SearchResults}
      </Box>
    </Box>
  );
};

export default MainSearchResults;
